import { Link, useLocation } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { usePunditUserContext } from "@/react/contexts/punditUserContext";
import { useAudienceInfiniteQuery } from "@/react/hooks/useAudienceInfiniteQuery";
import type { CommunityMember } from "@/react/types";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { AvatarWithProgress } from "../../Leaderboard/Dashboard/MyProfile/AvatarWithProgress";
import { useGamificationMember } from "../../Profile/HoverCard/useGamificationMember";
import { Icon } from "../../shared/Icon";
import { MemberGradientBackground } from "../../shared/MembersList/CommunityMembers/CardView/MemberGradientBackground";
import { ViewTypes } from "../../shared/MembersList/CommunityMembers/ListView/CommunityMember/MemberActions";
import { useSelectMembersInfiniteQuery } from "../../shared/MembersList/CommunityMembers/hooks/useSelectMembersInfiniteQuery";
import { useCurrentUserLocationInfo } from "../../shared/ProfileFields/FormFields/LocationNotices/useCurrentUserLocationInfo";
import { Typography } from "../../shared/uikit/Typography";
import { activeConnectionsFilter } from "../Connections/MyConnections";
import { useMemberDirectorySettings } from "../useMemberDirectorySettings";

const i18nBase = "members_directory.left_side_profile_navigation";

export const LeftSideProfileNavigation = () => {
  const { currentCommunityMember } = usePunditUserContext();
  const { platformLocationText } = useCurrentUserLocationInfo();
  const { pathname } = useLocation();

  const isAllMembersActive = pathname === "/members";
  const isMyConnectionsActive = pathname === "/members/connections";

  const memberDirectorySettings = useMemberDirectorySettings();

  const { totalMembers } = useSelectMembersInfiniteQuery({
    defaultSort: memberDirectorySettings.defaultSort,
    scopeResults: memberDirectorySettings.scope,
    viewType: ViewTypes.MEMBER_DIRECTORY,
  });

  const { data: gamificationMembers } = useGamificationMember({
    member_ids: currentCommunityMember?.id ? [currentCommunityMember.id] : [],
    enabled: !!currentCommunityMember?.id,
  });

  const gamificationMember = gamificationMembers?.[0];

  const { totalQueryAudience: connectionsCount } = useAudienceInfiniteQuery({
    filters: [activeConnectionsFilter],
    perPage: 1,
  });

  return (
    <div className="border-primary relative mx-2 mt-4 flex h-fit flex-col place-items-center rounded-2xl border md:sticky md:top-4 md:mx-0 md:mt-0">
      {currentCommunityMember && (
        <MemberGradientBackground
          member={currentCommunityMember as unknown as CommunityMember}
        />
      )}
      <div className="relative z-10 flex w-full flex-col justify-between gap-7 px-3 pb-3 pt-10">
        <div className="flex flex-col items-center justify-center gap-4 px-4 md:w-[272px]">
          <div style={{ aspectRatio: "1 / 1" }}>
            <AvatarWithProgress
              avatarUrl={currentCommunityMember?.avatar_url ?? ""}
              name={currentCommunityMember?.name ?? ""}
              progress={gamificationMember?.level_progress}
              level={gamificationMember?.current_level}
            />
          </div>

          <div className="flex w-full flex-col items-center justify-center gap-3">
            <div className="flex w-full flex-col items-center justify-center gap-1">
              <span className="text-darkest dark:text-light max-w-full truncate">
                <Typography.LabelMd weight="semibold">
                  {`${currentCommunityMember?.first_name} ${currentCommunityMember?.last_name}`}
                </Typography.LabelMd>
              </span>

              <span className="max-w-full">
                <Typography.BodySm truncate color="text-light">
                  {currentCommunityMember?.headline}
                </Typography.BodySm>
              </span>
            </div>
            {platformLocationText && (
              <div className="text-light flex min-w-0 max-w-full items-center justify-center gap-1">
                <Icon type="16-location" size={16} className="shrink-0" />
                <Typography.LabelXs truncate color="text-light">
                  {platformLocationText}
                </Typography.LabelXs>
              </div>
            )}
          </div>
        </div>

        <div className="flex w-full flex-col">
          <div className="border-primary m-2 h-px w-full border-b" />
          <Link
            to="/members"
            className={classNames(
              "hover:bg-secondary !text-dark flex h-10 w-full justify-start gap-2 overflow-hidden rounded-lg p-3 transition-colors",
              isAllMembersActive && "bg-secondary",
            )}
          >
            <div className="flex flex-1 justify-between">
              <Typography.LabelSm weight="medium" color="text-primary">
                {t(`${i18nBase}.all_members`)}
              </Typography.LabelSm>
              <Typography.BodyXs weight="normal" color="text-light">
                {totalMembers}
              </Typography.BodyXs>
            </div>
          </Link>

          <Link
            to="/members/connections"
            className={classNames(
              "hover:bg-secondary !text-dark flex h-10 w-full justify-start gap-2 overflow-hidden rounded-lg p-3 transition-colors",
              isMyConnectionsActive && "bg-secondary",
            )}
          >
            <div className="flex flex-1 justify-between">
              <Typography.LabelSm weight="medium" color="text-primary">
                {t(`${i18nBase}.my_connections`)}
              </Typography.LabelSm>
              <Typography.BodyXs weight="normal" color="text-light">
                {connectionsCount}
              </Typography.BodyXs>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};
